import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { FormField } from '../types/form';
import { 
  Type, 
  Hash, 
  Phone, 
  Mail, 
  ChevronDown, 
  CheckSquare, 
  Circle, 
  FileText, 
  Calendar, 
  Upload,
  Image,
  Table,
  Heading,
  ToggleLeft,
  Calculator,
  Video,
  Wifi,
  CalendarClock
} from 'lucide-react';

interface FormFieldPaletteProps {
  onAddField: (fieldType: FormField['field_type']) => void;
}

const fieldGroups = [
  {
    group: 'Basic Fields',
    fields: [
      {
        type: 'text' as const,
        label: 'Text Field',
        icon: Type,
        description: 'Single line text input'
      },
      {
        type: 'number' as const,
        label: 'Number Field',
        icon: Hash,
        description: 'Numeric input only'
      },
      {
        type: 'phone' as const,
        label: 'Phone Number',
        icon: Phone,
        description: 'Phone number input'
      },
      {
        type: 'email' as const,
        label: 'Email',
        icon: Mail,
        description: 'Email address input'
      },
      {
        type: 'textarea' as const,
        label: 'Text Area',
        icon: FileText,
        description: 'Multi-line text input'
      },
      {
        type: 'date' as const,
        label: 'Date',
        icon: Calendar,
        description: 'Date picker'
      }
    ]
  },
  {
    group: 'Selection Fields',
    fields: [
      {
        type: 'dropdown' as const,
        label: 'Dropdown',
        icon: ChevronDown,
        description: 'Select from options'
      },
      {
        type: 'checkbox' as const,
        label: 'Checkbox',
        icon: CheckSquare,
        description: 'Multiple selection'
      },
      {
        type: 'radio' as const,
        label: 'Radio Button',
        icon: Circle,
        description: 'Single selection'
      }
    ]
  },
  {
    group: 'Advanced Fields',
    fields: [
      {
        type: 'file' as const,
        label: 'File Upload',
        icon: Upload,
        description: 'File upload input'
      },
      {
        type: 'image_upload' as const,
        label: 'Image Upload',
        icon: Image,
        description: 'Image upload with preview'
      },
      {
        type: 'multi_row' as const,
        label: 'Multi-Row Fields',
        icon: Table,
        description: 'Dynamic rows with multiple fields'
      },
      {
        type: 'checkbox_with_input' as const,
        label: 'Checkbox + Input',
        icon: ToggleLeft,
        description: 'Checkbox with custom text/number input'
      },
      {
        type: 'logic' as const,
        label: 'Logic Field',
        icon: Calculator,
        description: 'Dynamic field that processes other field values'
      },
      {
        type: 'av_check' as const,
        label: 'Camera & Mic Check',
        icon: Video,
        description: 'Live camera preview and microphone level check'
      },
      {
        type: 'connection_check' as const,
        label: 'Connection Check',
        icon: Wifi,
        description: 'Measure latency, download, and upload speed'
      },
      {
        type: 'scheduler' as const,
        label: 'Scheduler',
        icon: CalendarClock,
        description: 'Embed a Cal.com booking calendar'
      }
    ]
  },
  {
    group: 'Content',
    fields: [
      {
        type: 'header' as const,
        label: 'Header',
        icon: Heading,
        description: 'Text header with font size options'
      },
      {
        type: 'image' as const,
        label: 'Image',
        icon: Image,
        description: 'Display images with size and layout options'
      }
    ]
  }
];

const FormFieldPalette: React.FC<FormFieldPaletteProps> = ({ onAddField }) => {
  return (
    <Card className="notion-card mt-6">
      <CardHeader>
        <CardTitle className="notion-section-title">Field Types</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {fieldGroups.map((group) => (
            <div key={group.group} className="space-y-3">
              <h3 className="notion-label text-muted-foreground font-medium">{group.group}</h3>
              <div className="space-y-2">
                {group.fields.map((fieldType) => {
                  const Icon = fieldType.icon;
                  return (
                    <Button
                      key={fieldType.type}
                      variant="outline"
                      className="w-full h-auto p-3 text-left justify-start overflow-hidden"
                      onClick={() => onAddField(fieldType.type)}
                    >
                      <div className="flex items-center space-x-3 overflow-hidden">
                        <Icon className="w-5 h-5 text-black dark:text-white" />
                        <div className="flex-1 min-w-0">
                          <div className="notion-label text-foreground">{fieldType.label}</div>
                          <div className="notion-caption text-muted-foreground mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap">{fieldType.description}</div>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default FormFieldPalette;